import Label from '../../Utils/FormInputs/Label';
import TextField from '../../Utils/FormInputs/TextField';
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Switch from '@material-ui/core/Switch';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Chip from '@material-ui/core/Chip';

const useStyles = makeStyles(() => ({
  root: { padding: 24 },
  sectionTitle: { fontSize: '1.05rem', fontWeight: 600, color: '#333', marginBottom: 16 },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  label: { fontWeight: 600, fontSize: '0.88rem', color: '#444', minWidth: 160 },
  timestamp: { fontSize: '0.82rem', color: '#1565C0', fontWeight: 500 },
  chipSigned: { backgroundColor: '#4CAF50', color: '#fff', fontWeight: 600 },
  chipMissing: { backgroundColor: '#F44336', color: '#fff', fontWeight: 600 },
  chipComplete: { backgroundColor: '#4CAF50', color: '#fff', fontWeight: 600 },
  chipPending: { backgroundColor: '#FF9800', color: '#fff', fontWeight: 600 },
  allergyChip: { margin: '0 4px', fontWeight: 500 },
  vitalsRow: { marginBottom: 16 },
  divider: {
    height: 1,
    backgroundColor: '#e0e0e0',
    margin: '20px 0',
  },
  escortSection: { marginTop: 8 },
}));

const DayCaseCheckIn = ({ surgery, data, onChange }) => {
  const classes = useStyles();

  const handleSwitch = (field) => (e) => {
    const value = e.target.checked;
    onChange(field, value);
    if (field === 'arrived' && value) {
      onChange('arrivalTime', new Date().toLocaleTimeString());
    }
  };

  const handleCheck = (field) => (e) => {
    onChange(field, e.target.checked);
  };

  const handleText = (field) => (e) => {
    onChange(field, e.target.value);
  };

  return (
    <Paper className={classes.root} elevation={0}>
      <Typography className={classes.sectionTitle}>Day-Case Check-In</Typography>

      {/* Patient Arrival */}
      <div className={classes.row}>
        <Typography className={classes.label}>Patient Arrival</Typography>
        <Switch
          checked={data.arrived || false}
          onChange={handleSwitch('arrived')}
          color="primary"
        />
        {data.arrivalTime && (
          <Typography className={classes.timestamp}>Arrived at {data.arrivalTime}</Typography>
        )}
      </div>

      {/* Identity Verified */}
      <div className={classes.row}>
        <FormControlLabel
          control={
            <Checkbox
              checked={data.identityVerified || false}
              onChange={handleCheck('identityVerified')}
              color="primary"
            />
          }
          label="Identity Verified (ID band + verbal confirmation)"
        />
      </div>

      {/* Fasting Confirmed */}
      <div className={classes.row}>
        <FormControlLabel
          control={
            <Checkbox
              checked={data.fastingConfirmed || false}
              onChange={handleCheck('fastingConfirmed')}
              color="primary"
            />
          }
          label="Fasting Confirmed"
        />
        {data.fastingConfirmed && (
          <TextField
            size="small"
            variant="outlined"
            placeholder="Hours since last meal"
            value={data.fastingHours || ''}
            onChange={handleText('fastingHours')}
            style={{ width: 200 }}
          />
        )}
      </div>

      {/* Consent Forms */}
      <div className={classes.row}>
        <Typography className={classes.label}>Consent Forms</Typography>
        <Chip
          label={data.consentSigned ? 'Signed' : 'Missing'}
          size="small"
          className={data.consentSigned ? classes.chipSigned : classes.chipMissing}
        />
      </div>

      {/* Pre-Op Assessment */}
      <div className={classes.row}>
        <Typography className={classes.label}>Pre-Op Assessment</Typography>
        <Chip
          label={data.preOpComplete ? 'Complete' : 'Pending'}
          size="small"
          className={data.preOpComplete ? classes.chipComplete : classes.chipPending}
        />
      </div>

      <div className={classes.divider} />

      {/* Quick Vitals */}
      <Typography className={classes.sectionTitle} style={{ marginTop: 8 }}>Quick Vitals</Typography>
      <Grid container spacing={2} className={classes.vitalsRow}>
        <Grid item xs={6} sm={3}>
          <TextField
            label="BP (mmHg)"
            size="small"
            variant="outlined"
            fullWidth
            value={data.bp || ''}
            onChange={handleText('bp')}
            placeholder="120/80"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="HR (bpm)"
            size="small"
            variant="outlined"
            fullWidth
            value={data.hr || ''}
            onChange={handleText('hr')}
            placeholder="72"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="SpO2 (%)"
            size="small"
            variant="outlined"
            fullWidth
            value={data.spo2 || ''}
            onChange={handleText('spo2')}
            placeholder="98"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Temp (°C)"
            size="small"
            variant="outlined"
            fullWidth
            value={data.temp || ''}
            onChange={handleText('temp')}
            placeholder="36.6"
          />
        </Grid>
      </Grid>

      <div className={classes.divider} />

      {/* Allergies Confirmed */}
      <div className={classes.row}>
        <FormControlLabel
          control={
            <Checkbox
              checked={data.allergiesConfirmed || false}
              onChange={handleCheck('allergiesConfirmed')}
              color="primary"
            />
          }
          label="Allergies Confirmed"
        />
        <Chip label="Penicillin" size="small" className={classes.allergyChip} color="secondary" />
        <Chip label="Latex" size="small" className={classes.allergyChip} color="secondary" />
      </div>

      <div className={classes.divider} />

      {/* Escort / Companion */}
      <Typography className={classes.sectionTitle}>Escort / Companion</Typography>
      <Grid container spacing={2} className={classes.escortSection}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Companion Name"
            size="small"
            variant="outlined"
            fullWidth
            required
            value={data.escortName || ''}
            onChange={handleText('escortName')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Contact Number"
            size="small"
            variant="outlined"
            fullWidth
            required
            value={data.escortContact || ''}
            onChange={handleText('escortContact')}
          />
        </Grid>
      </Grid>

      <div className={classes.divider} />

      {/* Belongings */}
      <div className={classes.row}>
        <FormControlLabel
          control={
            <Checkbox
              checked={data.belongingsSecured || false}
              onChange={handleCheck('belongingsSecured')}
              color="primary"
            />
          }
          label="Patient belongings secured and labeled"
        />
      </div>
    </Paper>
  );
};

export default DayCaseCheckIn;
